import React, {Component} from "react";
import {connect} from "react-redux";
import {BottomNavigation, BottomNavigationAction, IconButton, Slider, Stack, Tooltip} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TuneIcon from "@mui/icons-material/Tune";
import {ZoomIn, ZoomOut} from "@mui/icons-material";
import {updateDigitalZoomStatus} from "../../../redux_actions/maps.state.action";
import {checkIfDigitalZoom, getMaxZoomFor} from "../utils/zoomControlUtils";
import MagMappingDialog from "./MagMappingDialog";

const PURPLE = "rgba(51, 4, 112, 0.8)";
const GREEN = "#00f548";

/** Label between two levels, interpolated like the OL readout (zoomControl.getIntegerZoomLevel). */
export const interpolateZoomLabel = (zoomScale, z) => {
    if (!zoomScale?.length || !Number.isFinite(z)) return 0;
    const floor = Math.max(0, Math.min(zoomScale.length - 1, Math.floor(z)));
    const ceil = Math.min(zoomScale.length - 1, floor + 1);
    return zoomScale[floor] + (z - floor) * (zoomScale[ceil] - zoomScale[floor]);
};

/** Whole numbers near an integer, otherwise one decimal (4, 4.3, 7.5, 10). */
export const formatContinuousMag = (value) => {
    if (!Number.isFinite(value)) return "0";
    if (Math.abs(value - Math.round(value)) < 0.05) return String(Math.round(value));
    return String(Math.round(value * 10) / 10);
};

/**
 * Zoom control for OpenSeadragon maps: continuous readout, a bar with one stop per level
 * (labels after the organisation mapping) and the digital-zoom toggle. Dragging onto the
 * digital stop turns digital zoom on, like the OL "Enable Digital" button.
 */
class ZoomBarOSD extends Component {
    state = {openPanel: false, mappingOpen: false};

    getRange = () => {
        const {slidemap, zoomLevels, nativeTopLevel} = this.props;
        return {
            min: slidemap.getMinZoom(),
            nativeMax: getMaxZoomFor(zoomLevels, false, nativeTopLevel),
            digitalMax: getMaxZoomFor(zoomLevels, true, nativeTopLevel),
        };
    };

    setDigital = (enabled) => {
        const {slidemap, zoomLevels, nativeTopLevel, activeMapId} = this.props;
        slidemap.getView().setMaxZoom(getMaxZoomFor(zoomLevels, enabled, nativeTopLevel));
        this.props.dispatch(updateDigitalZoomStatus(activeMapId, enabled));
    };

    onBarChange = (_event, value) => {
        const {nativeMax} = this.getRange();
        if (value > nativeMax && !this.props.digitalZoomStatus) this.setDigital(true);
        this.props.slidemap.setZoomLevel(value, true);
    };

    /** One level is a ×2 step; z stays fractional, so there is no snapping. */
    zoomBy = (levels) => {
        const {slidemap, z} = this.props;
        const min = slidemap.getMinZoom();
        slidemap.setZoomLevel(Math.max(min, Math.min(slidemap.getMaxZoom(), (z ?? min) + levels)), false);
    };

    renderBar = () => {
        const {zoomScale, z, digitalZoomStatus, canConfigure} = this.props;
        const {min, nativeMax, digitalMax} = this.getRange();
        const marks = [];
        for (let idx = Math.ceil(min); idx <= digitalMax; idx++) {
            if (zoomScale[idx] != null) marks.push({value: idx, label: `${formatContinuousMag(zoomScale[idx])}×`});
        }

        return (
            <Box sx={{bgcolor: PURPLE, width: "80px", py: 1, display: "flex", flexDirection: "column", alignItems: "center"}}>
                {nativeMax < digitalMax && (
                    <IconButton size="small" onClick={() => this.setDigital(!digitalZoomStatus)}
                                sx={{color: GREEN, borderRadius: 1}}>
                        <Typography sx={{fontSize: 11, lineHeight: 1.2, textAlign: "center"}}>
                            {digitalZoomStatus ? "Disable" : "Enable"}<br/>Digital
                        </Typography>
                    </IconButton>
                )}
                <Box sx={{height: Math.max(120, marks.length * 26), py: 1}}>
                    <Slider
                        orientation="vertical"
                        value={Math.max(min, Math.min(digitalMax, z ?? min))}
                        min={Math.ceil(min)}
                        max={digitalMax}
                        step={0.01}
                        marks={marks}
                        onChange={this.onBarChange}
                        aria-label="Zoom magnification"
                        sx={{
                            color: GREEN,
                            "& .MuiSlider-markLabel": {color: "white", fontSize: 10, fontWeight: 600},
                            "& .MuiSlider-thumb": {width: 12, height: 12, border: "2px solid #fff"},
                        }}
                    />
                </Box>
                <Stack direction="row">
                    <IconButton size="small" onClick={() => this.zoomBy(-1)} sx={{color: "white"}}>
                        <ZoomOut/>
                    </IconButton>
                    <IconButton size="small" onClick={() => this.zoomBy(1)} sx={{color: "white"}}>
                        <ZoomIn/>
                    </IconButton>
                </Stack>
                {canConfigure && (
                    <Tooltip title="Magnification mapping" placement="left">
                        <IconButton size="small" onClick={() => this.setState({mappingOpen: true})} sx={{color: "white"}}>
                            <TuneIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        );
    };

    render() {
        const {zoomScale, z, zoomLevels, nativeTopLevel, slide} = this.props;
        const level = Math.round(z ?? 0);
        const isDigital = checkIfDigitalZoom(zoomLevels, level, nativeTopLevel);
        const textColor = isDigital ? "#ed6111" : this.state.openPanel ? GREEN : "white";

        return (
            <Stack direction="column-reverse" justifyContent={"flex-end"} className="zoomControl-forTour">
                <BottomNavigation sx={{borderRadius: 0, bgcolor: PURPLE, width: "80px"}}
                                  onChange={() => this.setState({openPanel: !this.state.openPanel})}>
                    <BottomNavigationAction icon={<>
                        <Typography variant={'h5'} align={"center"} gutterBottom={!isDigital} color={textColor}>
                            {formatContinuousMag(interpolateZoomLabel(zoomScale, z))}X
                        </Typography>
                        {isDigital ?
                            <Typography marginBottom={1} variant="caption" align={"center"} color={textColor}>
                                Digital
                            </Typography> : null}
                    </>}/>
                </BottomNavigation>
                {this.state.openPanel && this.renderBar()}
                {this.state.mappingOpen && (
                    <MagMappingDialog slide={slide} zoomLevels={zoomLevels} currentLevel={level}
                                      onClose={() => this.setState({mappingOpen: false})}/>
                )}
            </Stack>
        );
    }
}

const mapStateToProps = (state) => {
    const activeMapId = state.gammaStateReducer.activeMapId;
    const {zoomScale, z, digitalZoomStatus, zoomLevels, slidemap, nativeTopLevel, slide} = state.mapsStateReducer[activeMapId] ?? {};
    return {
        activeMapId, zoomScale, z, digitalZoomStatus, zoomLevels, slidemap, nativeTopLevel, slide,
        canConfigure: !!state.osdViewerConfigReducer?.permissions?.can_configure,
    };
};

export default connect(mapStateToProps)(ZoomBarOSD);
